<script setup>
import { computed, ref, watch } from "vue";
import { Link, router, usePage } from "@inertiajs/vue3";
import BrandLogo from "@/Components/Brand/BrandLogo.vue";
import DashboardMobileNav from "./DashboardMobileNav.vue";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { useModalogStore } from "@/stores/modalog.js";
import IconDropdownAccountSettings from "~/svg/dropdown-account-settings.svg";
import IconDropdownMyProfile from "~/svg/dropdown-my-profile.svg";
import IconDropdownLogOut from "~/svg/dropdown-log-out.svg";
import IconDropdownTour from "~/svg/dropdown-tour.svg";

const user = useAuthUser();
const page = usePage();

const menuOpen = ref(false);
const mobileNavOpen = ref(false);

// The mobile logo + hamburger only lift above the z-50 layer while the nav
// sheet is showing (through the end of its slide-out), so modalogs, which
// share z-50, cover the header like the rest of the page (BH-68).
const mobileNavLifted = ref(false);
watch(mobileNavOpen, (open) => {
    if (open) {
        mobileNavLifted.value = true;
    }
});

const myListsHref = computed(() =>
    user.value?.username
        ? route("user.giftlists", { username: user.value.username })
        : route("home"),
);

// Shared by HandleInertiaRequests so the badge shows on every page, not
// just the Dashboard (whose savedLists prop is capped for its sidebar).
const savedCount = computed(() => page.props.savedListsCount || 0);

// Friends nav sub-links (per Figma dropdown, node 269-695); shared by the
// desktop dropdown and the mobile menu.
const friendsLinks = [
    {
        label: "Find Friends",
        routeName: "friends.find-friends",
        component: "Friends/FindFriends",
    },
    {
        label: "Following",
        routeName: "friends.index",
        component: "Friends/Following",
    },
    {
        label: "Followers",
        routeName: "friends.followers",
        component: "Friends/Followers",
    },
];

// Active nav items keep the dark label and gain the amber underline
// (per Figma nav, node 224-3345); hover stays red.
const navItems = computed(() => [
    {
        label: "My Lists",
        href: myListsHref.value,
        active: page.component.startsWith("MyGiftHero/Wish"),
    },
    {
        label: "Saved Lists",
        href: route("bookmarks"),
        active: page.component === "MyGiftHero/Bookmarks",
        badge: savedCount.value,
    },
    {
        label: "Confirmed Gifts",
        href: route("gifts.for_you"),
        active: page.component.startsWith("MyGiftHero/ConfirmedGifts"),
    },
    {
        label: "Friends",
        href: route("friends.index"),
        active: page.component.startsWith("Friends/"),
        children: friendsLinks,
    },
    {
        label: "Gift Ideas",
        href: route("explore"),
        active: page.component.startsWith("Explore"),
    },
]);

// The explicit content utility matters: app.scss resets pseudo-element
// content to none, which beats the preflight's --tw-content default.
const NAV_UNDERLINE =
    "after:absolute after:inset-x-0 after:-bottom-1.5 after:h-[3px] after:rounded-[2px] after:bg-[#ffb545] after:content-['']";

function closeMenu() {
    menuOpen.value = false;
}

// The nav dropdown is hover/focus driven, and Inertia keeps this header
// mounted across navigations, so after a click the pointer is still over
// the item and the link keeps focus — the panel would stay open on the new
// page. Suppress the hover/focus reveal until the pointer leaves the item.
const dismissedDropdown = ref(null);

function dismissDropdown(label) {
    dismissedDropdown.value = label;
    document.activeElement?.blur?.();
}

function releaseDropdown(label) {
    if (dismissedDropdown.value === label) {
        dismissedDropdown.value = null;
    }
}

function logout() {
    router.post(route("logout"));
}

// Relaunch the onboarding tour (GH-1056) from the account menus. The store
// resolves lazily so header mounts (and tests) don't need Pinia active.
function openTour() {
    useModalogStore().showModalog({
        type: "ModalogOnboardingTour",
        props: {},
    });
}
</script>

<template>
    <header class="rounded-t-[10px] bg-white px-5 py-4 sm:px-9 sm:py-5">
        <!-- Mobile bar: full logo (left), boxed hamburger (right); the
             account entry lives in the menu (GH-1078, Figma 224-482). Both
             lift to z-[60] while the sheet is showing so they stay in place
             above it, the hamburger morphing into its ✕ (BH-36). -->
        <div
            class="relative flex items-center justify-between gap-4 xl:hidden"
            :class="{ 'z-[60]': mobileNavLifted }"
        >
            <Link
                :href="route('dashboard')"
                class="shrink-0"
                aria-label="Gift Hero"
                @click="mobileNavOpen = false"
            >
                <BrandLogo variant="dark" />
            </Link>

            <button
                type="button"
                class="flex size-[52px] shrink-0 appearance-none flex-col items-center justify-center gap-1.5 rounded-brand border border-solid border-[#dad9d2] bg-white"
                :aria-label="
                    mobileNavOpen ? 'Close navigation' : 'Open navigation'
                "
                :aria-expanded="mobileNavOpen"
                aria-controls="dashboard-mobile-nav"
                @click="mobileNavOpen = !mobileNavOpen"
            >
                <span
                    class="block h-[3px] w-[26px] rounded-full bg-brand-navy transition-transform duration-200"
                    :class="{ 'translate-y-[9px] rotate-45': mobileNavOpen }"
                ></span>
                <span
                    class="block h-[3px] w-[26px] rounded-full bg-brand-navy transition-opacity duration-200"
                    :class="{ 'opacity-0': mobileNavOpen }"
                ></span>
                <span
                    class="block h-[3px] w-[26px] rounded-full bg-brand-navy transition-transform duration-200"
                    :class="{ '-translate-y-[9px] -rotate-45': mobileNavOpen }"
                ></span>
            </button>
        </div>

        <!-- Desktop bar -->
        <div class="flex items-center justify-between gap-4 max-xl:hidden">
            <!-- Logo -->
            <Link :href="route('dashboard')" class="shrink-0">
                <BrandLogo variant="dark" />
            </Link>

            <!-- Primary nav (desktop) -->
            <nav class="max-xl:hidden xl:block">
                <ul
                    class="flex items-center gap-9 font-brand-display text-[17px] font-semibold uppercase tracking-wide text-[#102a3d] min-[1400px]:gap-[54px]"
                >
                    <!--
                        Nav links carry their color as a utility (not inherited):
                        the legacy stylesheet's bare `a { color: … }` rule beats
                        inheritance on pages that load app.scss.
                    -->
                    <li
                        v-for="item in navItems"
                        :key="item.label"
                        class="relative"
                        :class="dismissedDropdown === item.label ? '' : 'group'"
                        @mouseleave="releaseDropdown(item.label)"
                    >
                        <Link
                            :href="item.href"
                            class="relative inline-flex items-center gap-1.5 text-[#102a3d]"
                            :class="[
                                item.active ? NAV_UNDERLINE : '',
                                // Dropdown items stay red while the pointer is
                                // anywhere in the open dropdown (group hover),
                                // not just on the label itself.
                                item.children
                                    ? 'transition group-hover:text-brand-red group-focus-within:text-brand-red'
                                    : item.active
                                      ? ''
                                      : 'transition hover:text-brand-red',
                            ]"
                            :aria-haspopup="item.children ? 'menu' : undefined"
                            @click="
                                item.children && dismissDropdown(item.label)
                            "
                        >
                            {{ item.label }}
                            <svg
                                v-if="item.children"
                                viewBox="0 0 14 12"
                                class="h-2 w-2.5 shrink-0 text-brand-red transition-transform group-hover:rotate-180"
                                fill="currentColor"
                                stroke="currentColor"
                                stroke-width="2.5"
                                stroke-linejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M3 3 L11 3 L7 9.5 Z" />
                            </svg>
                            <!-- Stretches into a pill for two-digit counts. -->
                            <span
                                v-if="item.badge"
                                class="absolute -right-4 -top-4 grid h-[19px] min-w-[19px] place-items-center rounded-full bg-brand-red px-1.5 text-[11px] font-bold leading-none text-white"
                                >{{ item.badge }}</span
                            >
                        </Link>

                        <!--
                            Hover/focus dropdown (per Figma nav, node 269-695).
                            Padding-top bridges the gap below the label so the
                            pointer never leaves the hover area. Uses
                            invisible/opacity (not `hidden`): legacy .hidden is
                            !important on pages that load app.scss.
                        -->
                        <div
                            v-if="item.children"
                            class="invisible absolute left-1/2 top-full z-20 -translate-x-1/2 pt-3 opacity-0 transition group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
                        >
                            <ul
                                class="w-[155px] rounded-brand border border-solid border-brand-tan bg-white py-2 shadow-brand-hero"
                            >
                                <li
                                    v-for="child in item.children"
                                    :key="child.routeName"
                                >
                                    <Link
                                        :href="route(child.routeName)"
                                        class="block px-3 py-2.5 text-center font-brand text-[15px] font-semibold normal-case tracking-normal text-[#102a3d] transition"
                                        :class="
                                            page.component !== child.component
                                                ? 'hover:text-brand-red'
                                                : ''
                                        "
                                        @click="dismissDropdown(item.label)"
                                    >
                                        <span
                                            class="relative inline-block"
                                            :class="
                                                page.component ===
                                                    child.component &&
                                                NAV_UNDERLINE
                                            "
                                            >{{ child.label }}</span
                                        >
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>
            </nav>

            <!-- Account pill (desktop) -->
            <div
                v-click-outside="closeMenu"
                class="relative shrink-0 max-xl:hidden"
            >
                <!-- invisible (not hidden) while open: the pill still sizes
                     the header row, but its squarer corners can't peek out
                     from behind the panel's 10px radius. -->
                <button
                    type="button"
                    class="flex w-[321px] items-center gap-[18px] rounded-brand border border-solid border-brand-tan bg-white py-2.5 pl-3.5 pr-[27px] transition hover:bg-brand-cream/40"
                    :class="{ invisible: menuOpen }"
                    @click="menuOpen = !menuOpen"
                >
                    <img
                        :src="
                            user?.avatar?.square ?? user?.avatar?.profile_photo
                        "
                        :alt="user?.full_name"
                        class="size-10 rounded-[2px] object-cover"
                    />
                    <span
                        class="min-w-0 flex-1 truncate text-left font-brand text-[15px] font-semibold text-[#1d2333]"
                    >
                        Welcome, {{ user?.first_name }}
                    </span>
                    <svg
                        viewBox="0 0 14 12"
                        class="h-2.5 w-3 shrink-0 text-brand-red transition-transform"
                        :class="{ 'rotate-180': menuOpen }"
                        fill="currentColor"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M3 3 L11 3 L7 9.5 Z" />
                    </svg>
                </button>

                <!-- Inertia keeps this header mounted across navigations, so
                     every item closes the menu itself (GH-1004). Open state is
                     one connected card per the updated design: the panel
                     overlays the pill with its own header row (round avatar),
                     menu rows beneath; red row icons flip white with the
                     hover pill via currentColor (GH-1070). Icon wells center
                     on the avatar's 34px axis. -->
                <div
                    v-show="menuOpen"
                    class="absolute left-auto right-0 top-0 z-20 w-max min-w-full rounded-brand border border-solid border-brand-tan bg-white px-3.5 pb-3.5 pt-2.5 shadow-brand-hero"
                >
                    <button
                        type="button"
                        class="flex w-full items-center gap-[18px] pb-2.5 pr-[13px]"
                        @click="closeMenu"
                    >
                        <img
                            :src="
                                user?.avatar?.square ??
                                user?.avatar?.profile_photo
                            "
                            :alt="user?.full_name"
                            class="size-10 rounded-[2px] object-cover"
                        />
                        <span
                            class="min-w-0 flex-1 truncate text-left font-brand text-[15px] font-semibold text-[#1d2333]"
                        >
                            Welcome, {{ user?.first_name }}
                        </span>
                        <!-- Points up while open (Figma's down caret in the
                             open state was a design-file error). -->
                        <svg
                            viewBox="0 0 14 12"
                            class="h-2.5 w-3 shrink-0 rotate-180 text-brand-red"
                            fill="currentColor"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M3 3 L11 3 L7 9.5 Z" />
                        </svg>
                    </button>
                    <Link
                        :href="route('profile.show')"
                        class="group flex items-center gap-4 rounded-brand pl-2.5 pr-4 py-2.5 font-brand text-[15px] font-medium leading-6 text-[#1d2333] transition hover:bg-brand-red hover:text-white"
                        @click="closeMenu"
                    >
                        <span
                            class="grid w-5 shrink-0 place-items-center text-brand-red transition group-hover:text-white"
                            aria-hidden="true"
                        >
                            <IconDropdownAccountSettings
                                class="h-[19px] w-[19px]"
                            />
                        </span>
                        Account Settings
                    </Link>
                    <Link
                        :href="myListsHref"
                        class="group flex items-center gap-4 rounded-brand pl-2.5 pr-4 py-2.5 font-brand text-[15px] font-medium leading-6 text-[#1d2333] transition hover:bg-brand-red hover:text-white"
                        @click="closeMenu"
                    >
                        <span
                            class="grid w-5 shrink-0 place-items-center text-brand-red transition group-hover:text-white"
                            aria-hidden="true"
                        >
                            <IconDropdownMyProfile class="h-[19px] w-[19px]" />
                        </span>
                        My Profile
                    </Link>
                    <button
                        type="button"
                        class="group flex w-full items-center gap-4 rounded-brand pl-2.5 pr-4 py-2.5 text-left font-brand text-[15px] font-medium leading-6 text-[#1d2333] transition hover:bg-brand-red hover:text-white"
                        @click="
                            closeMenu();
                            openTour();
                        "
                    >
                        <span
                            class="grid w-5 shrink-0 place-items-center text-brand-red transition group-hover:text-white"
                            aria-hidden="true"
                        >
                            <IconDropdownTour
                                class="h-[18px] w-[15px]"
                                aria-hidden="true"
                            />
                        </span>
                        Gift Hero Tour
                    </button>
                    <button
                        type="button"
                        class="group flex w-full items-center gap-4 rounded-brand pl-2.5 pr-4 py-2.5 text-left font-brand text-[15px] font-medium leading-6 text-[#1d2333] transition hover:bg-brand-red hover:text-white"
                        @click="
                            closeMenu();
                            logout();
                        "
                    >
                        <span
                            class="grid w-5 shrink-0 place-items-center text-brand-red transition group-hover:text-white"
                            aria-hidden="true"
                        >
                            <IconDropdownLogOut class="h-[19px] w-[19px]" />
                        </span>
                        Log Out
                    </button>
                </div>
            </div>
        </div>

        <!-- Mobile nav sheet (BH-36, Figma 487-340) -->
        <DashboardMobileNav
            :open="mobileNavOpen"
            :items="navItems"
            :profile-href="myListsHref"
            @close="mobileNavOpen = false"
            @closed="mobileNavLifted = false"
        />
    </header>
</template>
